import { deepClone } from 'src/app/helpers/utils';
import {
  getAssignedEquipmentList,
  saveAssignEquipmentSuccess,
  saveReturnEquipmentSuccess,
  loadUserEquipments,
  clearEquipmentsToAssign,
  loadManageAssetTypes,
  addEquipmentToAssign
} from './../../../../core/store/equipment/equipment.actions';
import { AssignedEquipmentModel, EquipmentModel, EquipmentWithAssignmentsModel } from './../../../../models/equipmentsModel';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { Actions, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';
import { ModifiedSelectItem } from '../../../../models/utilModels';
import { getUsersIncludingTerminated, getUsersList } from '../../../../core/store/users/users.actions';
import { SelectItem } from 'primeng/api';
import { ManageAssetService } from 'src/app/core/services/manage-asset.service';
import { ManageAssetAssignmentSummaryModel } from 'src/app/models/manageAssetModels';

@Component({
    selector: 'app-list-equipment',
    templateUrl: './list-equipment.component.html',
    styleUrls: ['./list-equipment.component.scss'],
    standalone: false
})
export class ListEquipmentComponent extends BaseComponent implements OnInit {
  equipments: PagingResultsModel<EquipmentWithAssignmentsModel>;
  canEdit: boolean;
  loadingAssignedEquipmentList = true;
  filter: any;
  displayAssignEquipment;
  displayReturnEquipment;
  displayScanEquipment = false;
  scanBarcode = '';
  scanError = '';
  scanLoading = false;
  scanRows: { barcode: string; count: number; matches: ManageAssetAssignmentSummaryModel[] }[] = [];
  returnDialogUserID: number = null;
  returnDialogProgramID: number = null;
  returnDialogAssetAssignedID: number = null;
  returnDialogInitialSelection: AssignedEquipmentModel = null;
  returnDialogSelectionKey = 0;
  usersNames: any;
  usersList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  assignmentStateOptions: SelectItem[] = [
    { label: 'Assigned', value: 'assigned' },
    { label: 'Unassigned', value: 'unassigned' },
    { label: 'All', value: 'all' }
  ];
  groupFilterOptions: SelectItem[] = [{ label: 'No group filter', value: '' }];
  equipmentTypes: ModifiedSelectItem[];
  columnOptions: ModifiedSelectItem[] = [
    { label: 'Assigned To', value: 'assignedTo' },
    { label: 'Grouped', value: 'grouped' },
    { label: 'Type', value: 'type' },
    { label: 'Category', value: 'category' },
    { label: 'Description', value: 'description' },
    { label: 'Serial', value: 'serial' },
    { label: 'Barcode', value: 'barcode' },
    { label: 'Assigned From/Main Location', value: 'location' },
    { label: 'Expected Return Date', value: 'returnDate' },
    { label: 'Returned', value: 'returned' },
    { label: 'Status', value: 'status' },
    { label: 'Assign Signed', value: 'assignSigned' }
  ];
  visibleColumnKeys: string[] = this.columnOptions.map(x => x.value);
  rowsAmount = 25;
  includeTerminated = false;
  filterAssignmentState = 'assigned';
  filterAssignedToUserID: number = null;
  filterTypeId: number = 0;
  filterAssetCategory = '';
  filterDescription = '';
  filterSerialNumber = '';
  filterBarcode = '';
  filterLocation = '';
  filterReturnDate = '';
  filterGroup = '';
  private knownGroupNames = new Set<string>();
  private activeUsers = [];
  private usersIncludingTerminated = [];

  constructor(private actions$: Actions,
    private store: Store<State>,
    private manageAssetService: ManageAssetService,
    ) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3058); //Equipment Manage
      }),
      this.store.select(s => s.equipmentState.assignedEquipmentList).subscribe(assignedEquipmentList => {
        this.equipments = assignedEquipmentList;
        this.mergeGroupFilterOptionsFromRows(assignedEquipmentList?.result ?? []);
      }),
      this.store.select(s => s.equipmentState.loadingAssignedEquipmentList).subscribe(loadingAssignedEquipmentList => {
        this.loadingAssignedEquipmentList = loadingAssignedEquipmentList;
      }),
      this.store.select(s => s.equipmentState.manageAssetTypes).subscribe(equipmentTypes => {
        const equipmentTypesMapped = equipmentTypes && equipmentTypes.length > 0 ? equipmentTypes.map(c => ({ label: c.assetType, value: c.id }))
          : [{ label: 'loading...', value: undefined }];
        equipmentTypesMapped.unshift({ label: 'All', value: 0 });
        this.equipmentTypes = equipmentTypesMapped;
      }),
      this.store.select(s => s.usersState.usersList).subscribe(usersList => {
        this.activeUsers = usersList ?? [];
        this.updateUsersList();
        this.updateUsersNames();
      }),
      this.store.select(s => s.usersState.usersListIncludingTerminated).subscribe(usersListIncludingTerminated => {
        this.usersIncludingTerminated = usersListIncludingTerminated ?? [];
        this.updateUsersList();
        this.updateUsersNames();
      }),
      this.actions$.pipe(
        ofType(saveAssignEquipmentSuccess),
        tap(() => {
          this.savedAssignEquipment();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(saveReturnEquipmentSuccess),
        tap(() => {
          this.savedReturnEquipment();
        })
      ).subscribe(),
      this.manageAssetService.getAssetGroups().subscribe(groups => {
        this.mergeGroupFilterOptions(groups ?? []);
      }),
    );
    this.store.dispatch(getUsersList());
    this.store.dispatch(getUsersIncludingTerminated());
    this.store.dispatch(loadManageAssetTypes());
    this.loadPage({
      first: 0,
      rows: 25,
      sortField: 'returnDate',
      sortOrder: 1
    });
  }

  loadPage(event) {
    this.rowsAmount = event?.rows ?? this.rowsAmount ?? 25;
    this.filter = {
      ...(event ?? {}),
      rows: event?.rows ?? this.rowsAmount,
      filterAssignmentState: this.filterAssignmentState || 'assigned',
      filterUserId: this.filterAssignedToUserID ?? 0,
      filterTypeId: this.filterTypeId ?? 0,
      filterAssetCategory: this.filterAssetCategory || undefined,
      filterDescription: this.filterDescription || undefined,
      filterSerial: this.filterSerialNumber || undefined,
      filterBarcode: this.filterBarcode || undefined,
      filterLocation: this.filterLocation || undefined,
      filterReturnDate: this.filterReturnDate || undefined,
      filterGroup: this.filterGroup || undefined
    };
    this.store.dispatch(getAssignedEquipmentList({
      filter: deepClone(this.filter)
    }));
  }

  applyFilters() {
    this.loadPage({
      first: 0,
      rows: this.rowsAmount,
      sortField: this.filter?.sortField || 'returnDate',
      sortOrder: this.filter?.sortOrder ?? 1
    });
  }

  clearFilters() {
    this.includeTerminated = false;
    this.filterAssignmentState = 'assigned';
    this.filterAssignedToUserID = null;
    this.filterTypeId = 0;
    this.filterAssetCategory = '';
    this.filterDescription = '';
    this.filterSerialNumber = '';
    this.filterBarcode = '';
    this.filterLocation = '';
    this.filterReturnDate = '';
    this.filterGroup = '';
    this.updateUsersList();
    this.applyFilters();
  }

  isColumnVisible(columnKey: string) {
    return this.visibleColumnKeys.includes(columnKey);
  }

  editColumns(columnKey: string) {
    if (this.isColumnVisible(columnKey)) {
      if (this.visibleColumnKeys.length === 1) {
        return;
      }

      this.visibleColumnKeys = this.visibleColumnKeys.filter(x => x !== columnKey);
      return;
    }

    this.visibleColumnKeys = [...this.visibleColumnKeys, columnKey];
  }

  includeTerminatedChange(event) {
    this.includeTerminated = !!event?.checked;
    this.updateUsersList();
  }

  private updateUsersList() {
    const source = this.includeTerminated ? this.usersIncludingTerminated : this.activeUsers;
    this.usersList = source && source.length > 0
      ? source.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u.id }))
      : [{ label: 'loading...', value: undefined }];
    this.usersList.unshift({ label: '', value: null });
  }

  private updateUsersNames() {
    const users = (this.usersIncludingTerminated && this.usersIncludingTerminated.length > 0)
      ? this.usersIncludingTerminated
      : this.activeUsers;

    this.usersNames = users ?
      users.reduce((acc, cur) => {
        acc[cur.id] = cur.firstName + ' ' + cur.lastName;
        return acc;
      }, {})
      : [];
  }

  openAssignEquipment() {
    this.store.dispatch(clearEquipmentsToAssign());
    this.displayAssignEquipment = true;
  }

  openScanEquipment() {
    this.scanBarcode = this.filterBarcode || '';
    this.scanError = '';
    this.scanRows = [];
    this.displayScanEquipment = true;
  }

  onScanDialogHide() {
    this.scanError = '';
  }

  applyScanSearch() {
    if (this.scanLoading) {
      return;
    }
    const codes = (this.scanBarcode || '').split(/[\s,;]+/).filter(Boolean);
    if (!codes.length || codes.length > 100) {
      this.scanError = 'Enter between 1 and 100 barcodes.';
      return;
    }
    this.scanError = '';
    this.scanRows = [];
    if (codes.length === 1) {
      this.filterBarcode = codes[0];
      this.filterAssignmentState = 'all';
      this.displayScanEquipment = false;
      this.applyFilters();
      return;
    }
    this.scanLoading = true;
    const submittedValue = this.scanBarcode;
    this.manageAssetService.getAssignedAssets({ barcodes: codes, assignmentState: 'all' }).subscribe({
      next: response => {
        if (!this.displayScanEquipment || this.scanBarcode !== submittedValue) {
          return;
        }
        const matches = response?.result ?? [];
        const uniqueCodes = [...new Set(codes.map(code => code.replace(/-/g, '').toUpperCase()))];
        this.scanRows = uniqueCodes.map(normalized => ({
          barcode: codes.find(code => code.replace(/-/g, '').toUpperCase() === normalized),
          count: codes.filter(code => code.replace(/-/g, '').toUpperCase() === normalized).length,
          matches: matches.filter(item => [item.barcode, item.assetGUID]
            .some(code => (code || '').replace(/-/g, '').toUpperCase() === normalized))
        }));
      },
      error: err => {
        this.scanError = typeof err?.error === 'string' ? err.error : err?.error?.message || 'Unable to look up scanned assets.';
        this.scanLoading = false;
      },
      complete: () => { this.scanLoading = false; }
    });
  }

  openAssignEquipmentForItem(equipment: EquipmentWithAssignmentsModel) {
    if (!equipment?.equipment) {
      return;
    }

    this.store.dispatch(clearEquipmentsToAssign());
    this.store.dispatch(addEquipmentToAssign({ assignedEquipment: this.buildAssignQueueItem(equipment.equipment, equipment.lastAssignedEquipment) }));
    this.displayAssignEquipment = true;
  }

  openReturnEquipment(equipment: EquipmentWithAssignmentsModel = null) {
    this.displayReturnEquipment = true;
    if (equipment && equipment.lastAssignedEquipment) {
      this.returnDialogUserID = equipment.lastAssignedEquipment.userID;
      this.returnDialogProgramID = equipment.lastAssignedEquipment.assignedProgramID;
      this.returnDialogAssetAssignedID = equipment.lastAssignedEquipment.assetAssignedID ?? equipment.lastAssignedEquipment.id;
      this.returnDialogInitialSelection = this.buildReturnInitialSelection(equipment);
      this.returnDialogSelectionKey += 1;
      if (equipment.lastAssignedEquipment.userID) {
        this.store.dispatch(loadUserEquipments({ userID: equipment.lastAssignedEquipment.userID }));
      } else if (equipment.lastAssignedEquipment.assignedProgramID) {
        this.store.dispatch(loadUserEquipments({ assignedProgramID: equipment.lastAssignedEquipment.assignedProgramID }));
      }
      return;
    }

    this.returnDialogUserID = null;
    this.returnDialogProgramID = null;
    this.returnDialogAssetAssignedID = null;
    this.returnDialogInitialSelection = null;
    this.returnDialogSelectionKey += 1;
  }

  savedReturnEquipment() {
    this.displayReturnEquipment = false;
    this.loadPage(this.filter);
  }

  savedAssignEquipment() {
    this.displayAssignEquipment = false;
    this.loadPage(this.filter);
  }

  canReturn(equipment: EquipmentWithAssignmentsModel) {
    return this.canEdit
      && !!equipment?.lastAssignedEquipment?.assetAssignedID
      && !this.isUnavailableListing(equipment);
  }

  canAssign(equipment: EquipmentWithAssignmentsModel) {
    return this.canEdit
      && !equipment?.lastAssignedEquipment?.assetAssignedID
      && !this.isUnavailableListing(equipment);
  }

  getAssignedToLabel(equipment: EquipmentWithAssignmentsModel) {
    const assignedEquipment = equipment?.lastAssignedEquipment;
    return assignedEquipment?.assignedToUserName
      || assignedEquipment?.programName
      || (assignedEquipment?.assignedProgramID ? 'Assigned Campus' : '-');
  }

  getReturnedByLabel(equipment: EquipmentWithAssignmentsModel) {
    const returnedByUserID = equipment?.lastAssignedEquipment?.returnedToUserID;
    return returnedByUserID ? (this.usersNames?.[returnedByUserID] || `User #${returnedByUserID}`) : '-';
  }

  /** Row is tied to an active OP_AssetAssigned (not unassigned inventory / available line). */
  hasActiveAssignment(equipment: EquipmentWithAssignmentsModel): boolean {
    const id = equipment?.lastAssignedEquipment?.assetAssignedID;
    return id != null && id > 0;
  }

  hasReturnedDetails(equipment: EquipmentWithAssignmentsModel) {
    return !!equipment?.lastAssignedEquipment?.dateReturned;
  }

  isCampusAssignment(equipment: EquipmentWithAssignmentsModel): boolean {
    const assignment = equipment?.lastAssignedEquipment;
    return !!assignment?.assignedProgramID && !assignment?.userID;
  }

  getAssignSignedDisplay(equipment: EquipmentWithAssignmentsModel): string {
    if (!this.hasActiveAssignment(equipment)) {
      return '-';
    }
    if (this.isCampusAssignment(equipment)) {
      return 'Campus';
    }
    return equipment?.lastAssignedEquipment?.assignSigned ? 'Yes' : 'No';
  }

  isGoodReturnStatus(equipment: EquipmentWithAssignmentsModel) {
    return equipment?.lastAssignedEquipment?.assetReturnStatusID === 1;
  }

  isDamagedReturnStatus(equipment: EquipmentWithAssignmentsModel) {
    return equipment?.lastAssignedEquipment?.assetReturnStatusID === 2;
  }

  getGroupDisplay(equipment: EquipmentWithAssignmentsModel) {
    const groups = this.getAssetGroups(equipment);
    return groups.length ? groups.join(', ') : 'No';
  }

  private getAssetGroups(equipment: EquipmentWithAssignmentsModel): string[] {
    const groups = equipment?.lastAssignedEquipment?.equipment?.assetGroups
      || equipment?.equipment?.assetGroups
      || [];
    return groups.filter(x => !!x);
  }

  private mergeGroupFilterOptions(groups: string[]): void {
    const normalizedGroups = (groups ?? [])
      .map(group => `${group ?? ''}`.trim())
      .filter(group => group.length > 0);
    normalizedGroups.forEach(group => this.knownGroupNames.add(group));
    if (this.filterGroup) {
      this.knownGroupNames.add(this.filterGroup);
    }

    const sortedGroups = Array.from(this.knownGroupNames).sort((a, b) => a.localeCompare(b));
    this.groupFilterOptions = [
      { label: 'No group filter', value: '' },
      ...sortedGroups.map(group => ({ label: group, value: group }))
    ];
  }

  private mergeGroupFilterOptionsFromRows(rows: EquipmentWithAssignmentsModel[]): void {
    const rowGroups = (rows ?? [])
      .reduce((acc, row) => {
        acc.push(...this.getAssetGroups(row));
        return acc;
      }, [] as string[]);
    this.mergeGroupFilterOptions(rowGroups);
  }

  private buildAssignQueueItem(equipment: EquipmentModel, lastAssignedEquipment?: AssignedEquipmentModel): AssignedEquipmentModel {
    return {
      equipmentID: equipment.id,
      assetInventoryID: equipment.assetInventoryID ?? equipment.id,
      assetItemID: equipment.assetItemID,
      isInventoryAssignment: equipment.isInventoryAssignment,
      description: equipment.description,
      assetCategory: equipment.assetCategory,
      barcode: equipment.barcode,
      inventoryAssetLocation: lastAssignedEquipment?.inventoryAssetLocation || equipment.inventoryAssetLocation,
      assetStatus: lastAssignedEquipment?.assetStatus || 'Available',
      equipment
    };
  }

  private buildReturnInitialSelection(equipment: EquipmentWithAssignmentsModel): AssignedEquipmentModel {
    const assignedEquipment = equipment.lastAssignedEquipment;
    return {
      ...deepClone(assignedEquipment),
      assignedToUserName: assignedEquipment?.assignedToUserName ?? undefined,
      actionLabel: assignedEquipment?.assignedToUserName
        ? `Return from ${assignedEquipment.assignedToUserName}`
        : (assignedEquipment?.programName ? `Return from ${assignedEquipment.programName}` : 'Return to General Inventory')
    };
  }

  private isUnavailableListing(equipment: EquipmentWithAssignmentsModel): boolean {
    if (this.hasActiveAssignment(equipment)) {
      return false;
    }

    const status = `${equipment?.lastAssignedEquipment?.assetStatus ?? ''}`.trim().toLowerCase();
    return status.length > 0 && status !== 'available';
  }

}


